import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

import {
  AtSign,
  Calculator,
  Globe,
  Mail,
  MessageSquare,
  Send,
  Sparkles,
  User,
  Workflow,
} from "lucide-react";
import {
  about,
  hero,
  hobbies,
  person,
  pillars,
  resumeUrl,
} from "../mock";
import { toast } from "../hooks/use-toast";

function formatTime(date, timeZone) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone,
  })
    .formatToParts(date)
    .reduce((acc, p) => {
      acc[p.type] = p.value;
      return acc;
    }, {});

  return {
    hh: parts.hour ?? "00",
    mm: parts.minute ?? "00",
    ss: parts.second ?? "00",
  };
}

function iconForPillar(name) {
  switch (name) {
    case "calculator":
      return Calculator;
    case "sparkles":
      return Sparkles;
    case "globe":
      return Globe;
    case "workflow":
      return Workflow;
    default:
      return Sparkles;
  }
}

function useRevealOnScroll() {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll("[data-reveal]"));
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("is-revealed");
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

function TimezoneSelect({ timezones, value, onChange }) {
  return (
    <Select value={value.id} onValueChange={(v) => onChange(timezones.find((z) => z.id === v) ?? value)}>
      <SelectTrigger className="timezone-dropdown-modern" aria-label="Timezone">
        <SelectValue placeholder={value.label} />
      </SelectTrigger>
      <SelectContent>
        {timezones.map((z) => (
          <SelectItem key={z.id} value={z.id}>
            {z.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}


function HeroBackgroundCanvas() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let raf = 0;
    const DPR = Math.min(2, window.devicePixelRatio || 1);

    const particles = Array.from({ length: 120 }).map(() => ({
      x: Math.random(),
      y: Math.random(),
      r: 0.4 + Math.random() * 1.4,
      vx: (Math.random() - 0.5) * 0.06,
      vy: (Math.random() - 0.5) * 0.05,
      hue: 320 + Math.random() * 30,
    }));

    function resize() {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = Math.floor(w * DPR);
      canvas.height = Math.floor(h * DPR);
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    }

    function draw(t) {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;

      // background wash
      ctx.clearRect(0, 0, w, h);
      const g = ctx.createRadialGradient(
        w * 0.25,
        h * 0.2,
        0,
        w * 0.25,
        h * 0.2,
        Math.max(w, h)
      );
      // restrained finance palette (steel blue)
      g.addColorStop(0, "rgba(124, 140, 166, 0.10)");
      g.addColorStop(0.5, "rgba(17, 24, 36, 0.06)");
      g.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);

      // particles
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -0.1) p.x = 1.1;
        if (p.x > 1.1) p.x = -0.1;
        if (p.y < -0.1) p.y = 1.1;
        if (p.y > 1.1) p.y = -0.1;

        const px = p.x * w;
        const py = p.y * h;
        const alpha = 0.10 + 0.06 * Math.sin(t / 900 + p.x * 5);

        ctx.beginPath();
        ctx.fillStyle = `rgba(124, 140, 166, ${alpha})`;
        ctx.arc(px, py, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = requestAnimationFrame(draw);
    }

    resize();
    raf = requestAnimationFrame(draw);

    const onResize = () => resize();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(raf);
    };
  }, []);

  return <canvas ref={ref} className="hero-canvas" />;
}

function FinanceSparkline() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const DPR = Math.min(2, window.devicePixelRatio || 1);

    // Generate a gentle random-walk series (finance-like pulse)
    let series = Array.from({ length: 48 }).map((_, i) => 0.52 + 0.08 * Math.sin(i / 6));

    let raf = 0;
    let lastT = 0;

    function resize() {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = Math.floor(w * DPR);
      canvas.height = Math.floor(h * DPR);
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    }

    function step(t) {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;

      const dt = Math.min(60, t - lastT || 16);
      lastT = t;

      // shift slowly ~ every 90ms
      if (dt && t % 90 < 18) {
        const prev = series[series.length - 1];
        const noise = (Math.random() - 0.5) * 0.06;
        const next = Math.max(0.18, Math.min(0.86, prev + noise));
        series = [...series.slice(1), next];
      }

      ctx.clearRect(0, 0, w, h);

      // subtle grid
      ctx.strokeStyle = "rgba(124, 140, 166, 0.10)";
      ctx.lineWidth = 1;
      for (let gx = 0; gx <= 6; gx++) {
        const x = (gx / 6) * w;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }

      // line
      const accent = "rgba(124, 140, 166, 0.85)";
      ctx.strokeStyle = accent;
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      series.forEach((v, i) => {
        const x = (i / (series.length - 1)) * w;
        const y = h - v * h;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();

      // last point glow
      const lx = w;
      const ly = h - series[series.length - 1] * h;
      const g = ctx.createRadialGradient(lx, ly, 0, lx, ly, 16);
      g.addColorStop(0, "rgba(124, 140, 166, 0.28)");
      g.addColorStop(1, "rgba(124, 140, 166, 0)");
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(lx, ly, 16, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = "rgba(255, 255, 255, 0.92)";
      ctx.beginPath();
      ctx.arc(lx, ly, 2.2, 0, Math.PI * 2);
      ctx.fill();

      raf = requestAnimationFrame(step);
    }

    resize();
    raf = requestAnimationFrame(step);

    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className="finance-sparkline" aria-label="Market pulse">
      <canvas ref={ref} className="finance-sparkline__canvas" />
    </div>
  );
}

function TimeWidget({ nightMode, onToggleNightMode }) {
  const timezones = useMemo(
    () => [
      {
        id: "Europe/Paris",
        label: "GMT+1",
      },
      {
        id: "Asia/Kolkata",
        label: "IST",
      },
      {
        id: "UTC",
        label: "UTC",
      },
    ],
    []
  );

  const [tz, setTz] = useState(timezones[0]);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 250);
    return () => clearInterval(t);
  }, []);

  const { hh, mm, ss } = formatTime(now, tz.id);

  return (
    <div className="time-widget-modern" aria-label="Time widget">
      <div className="time-main">
        <span className="time-hours">{hh}</span>
        <span className="time-separator">:</span>
        <span className="time-minutes">{mm}</span>
        <span className="time-seconds">:{ss}</span>
      </div>
      <div className="timezone-info">
        <div className="timezone-location">Based in {person.location}</div>
        <div className="custom-dropdown">
          {/* Using shadcn Select instead of native select */}
          <div style={{ minWidth: 96 }}>
            <TimezoneSelect timezones={timezones} value={tz} onChange={setTz} />
          </div>
        </div>
        <button
          className="night-mode-toggle"
          onClick={onToggleNightMode}
          aria-label={nightMode ? "Switch to light mode" : "Switch to dark mode"}
          title={nightMode ? "Switch to light mode" : "Switch to dark mode"}
        >
          <span className="sun-icon">{nightMode ? "☀" : "☾"}</span>
        </button>
      </div>
    </div>
  );
}

function LuxeContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const API = `${BACKEND_URL}/api`;

      const res = await fetch(`${API}/contact/submit`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          subject: form.subject,
          message: form.message,
          source: "portfolio",
        }),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Formspree request failed");
      }

      setForm({ name: "", email: "", subject: "", message: "" });
      toast({
        title: "Message sent",
        description: "Thanks. I'll get back to you soon.",
      });
    } catch (err) {
      toast({
        title: "Couldn’t send message",
        description: "Please try again, or email me directly.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="contact-form-centered" data-reveal>
      <form className="contact-form-luxe" onSubmit={submit}>
        <div className="form-row">
          <div className="form-group-luxe">
            <label htmlFor="name" className="form-label-luxe">
              <User size={16} />
              <span>Your Name</span>
            </label>
            <input
              className="form-input-luxe"
              id="name"
              value={form.name}
              onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
              placeholder="John Doe"
              required
            />
          </div>
          <div className="form-group-luxe">
            <label htmlFor="email" className="form-label-luxe">
              <AtSign size={16} />
              <span>Email Address</span>
            </label>
            <input
              className="form-input-luxe"
              id="email"
              type="email"
              value={form.email}
              onChange={(e) =>
                setForm((s) => ({ ...s, email: e.target.value }))
              }
              placeholder="john@example.com"
              required
            />
          </div>
        </div>

        <div className="form-group-luxe" style={{ marginTop: 16 }}>
          <label htmlFor="subject" className="form-label-luxe">
            <MessageSquare size={16} />
            <span>Subject</span>
          </label>
          <input
            className="form-input-luxe"
            id="subject"
            value={form.subject}
            onChange={(e) =>
              setForm((s) => ({ ...s, subject: e.target.value }))
            }
            placeholder="What would you like to discuss?"
            required
          />
        </div>

        <div className="form-group-luxe" style={{ marginTop: 16 }}>
          <label htmlFor="message" className="form-label-luxe">
            <Mail size={16} />
            <span>Message</span>
          </label>
          <textarea
            className="form-textarea-luxe"
            id="message"
            rows={5}
            value={form.message}
            onChange={(e) =>
              setForm((s) => ({ ...s, message: e.target.value }))
            }
            placeholder="Tell me about your project, opportunity, or just say hello..."
            required
          />
        </div>

        <button className="form-submit-luxe" type="submit" disabled={sending}>
          <span style={{ display: "inline-flex", gap: 10, alignItems: "center" }}>
            <span>{sending ? "Sending…" : "Send Message"}</span>
            <Send size={18} />
          </span>
        </button>

        <div style={{ marginTop: 14, color: "rgba(255,255,255,0.55)", fontSize: 12 }}>
          Or email directly: <a href={`mailto:${person.email}`}>{person.email}</a>
        </div>
      </form>
    </div>
  );
}

export default function Home() {
  useRevealOnScroll();

  const [nightMode, setNightMode] = useState(() => {
    try {
      return localStorage.getItem("ap_theme") !== "light";
    } catch {
      return true;
    }
  });
  const [activeHobby, setActiveHobby] = useState(null);

  const heroRef = useRef(null);
  const pillarsTrackRef = useRef(null);
  const glowRef = useRef(null);
  const contactRef = useRef(null);
  const cursorGlowRef = useRef(null);

  // Tagline is static now (no rotating words)

  // Core Financial Skills: horizontal carousel scroll (wheel + touch)
  useEffect(() => {
    const el = pillarsTrackRef.current;
    if (!el) return;

    const onWheel = (e) => {
      if (e.shiftKey) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      if (el.scrollWidth <= el.clientWidth + 1) return;

      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Reduced mouse-tracking effects for a more serious finance-first feel

  useEffect(() => {
    // Apply theme globally (affects all routes)
    const root = document.documentElement;
    root.classList.toggle("theme-dark", nightMode);
    root.classList.toggle("theme-light", !nightMode);
    try {
      localStorage.setItem("ap_theme", nightMode ? "dark" : "light");
    } catch {
      // ignore
    }
  }, [nightMode]);

  const hobbyFocus =
    activeHobby === null
      ? { name: hobbies.title, detail: "Hover to reveal." }
      : hobbies.items[activeHobby];

  // Tagline options (generated; we implement the strongest one)
  // 1) From data to investment decisions.
  // 2) Analyzing risk. Allocating capital.
  // 3) Research-driven. Market-aware.

  return (
    <div className={`page ${nightMode ? "night-mode" : ""}`}>
      <section id="home" className="hero-luxury" ref={heroRef}>
        <div className="hero-canvas-wrap" aria-hidden="true">
          <HeroBackgroundCanvas />
        </div>

        <TimeWidget
          nightMode={nightMode}
          onToggleNightMode={() => setNightMode((v) => !v)}
        />

        <div className="hero-tagline-wrapper">
          <h1 className="hero-tagline-luxury">
            <div className="tagline-line">From data to investment decisions.</div>
            <div className="tagline-line tagline-line--sub">Research-minded. Model-driven. Clear about risk.</div>
          </h1>

          {/* Removed hero sparkline widget to avoid confusion with global background animation */}


          <div style={{ marginTop: 22, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <a
              href={resumeUrl}
              target="_blank"
              rel="noreferrer"
              className="luxe-nav__link hero-cta-link"
              style={{ textDecoration: "none" }}
            >
              Download CV
            </a>
            <a
              href={person.linkedinUrl}
              target="_blank"
              rel="noreferrer"
              className="luxe-nav__link"
            >
              LinkedIn
            </a>
            <a
              href={`mailto:${person.email}`}
              className="luxe-nav__link"
              onClick={(e) => {
                // keep mailto but also a micro toast
                toast({ title: "Email", description: person.email });
              }}
            >
              Email
            </a>
          </div>
        </div>

        <div className="static-bubble" aria-hidden="true" />

        {/* Market pulse moved under headline */}

        {/* Removed Pink Noise element for a finance-first homepage */}
      </section>

      <section id="about" className="about-immersive" style={{ position: "relative" }}>
        <div className="about-immersive__backdrop" aria-hidden="true">
          <div className="about-immersive__glow" ref={glowRef} />
          <div className="about-immersive__gradient" />
          <div className="about-immersive__overlay" />
        </div>

        <div className="about-immersive__container">
          <div className="about-immersive__hero" data-reveal>
            <div className="about-immersive__hero-copy">
              <span className="about-immersive__label">{about.label}</span>
            {/* pillars count is provided via inline style on the wrapper */}

              <h2 className="about-immersive__title">
                {about.titleLines.map((l) => (
                  <span
                    key={l.text}
                    className={
                      l.accent
                        ? "about-immersive__title-line about-immersive__title-line--accent"
                        : "about-immersive__title-line"
                    }
                  >
                    {l.text}
                  </span>
                ))}
              </h2>
              <p className="about-immersive__intro">{about.intro}</p>
            </div>

            <div className="about-immersive__hero-panel" data-reveal>
              <div className="about-immersive__portrait-frame">
                <img
                  src="/product/photos/amrith-hero.jpg"
                  alt="Amrith Pawar — Portrait"
                  className="about-immersive__portrait"
                  loading="lazy"
                />
                <div className="about-immersive__portrait-identity">
                  <span className="about-immersive__portrait-name">
                    {about.portrait.name}
                  </span>
                  <span className="about-immersive__portrait-title">
                    {about.portrait.title}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="pillars-carousel" data-reveal>
            <div className="pillars-carousel__header">
              <div className="pillars-scrollpin__eyebrow">{pillars.eyebrow}</div>
            </div>

            <div className="pillars-carousel__track" ref={pillarsTrackRef}>
              {pillars.items.map((it) => {
                const Icon = iconForPillar(it.icon);
                return (
                  <article
                    key={it.title}
                    className="pillars-scrollpin__content pillars-carousel__card"
                    style={{
                      "--card-accent": it.accent,
                      "--card-soft": it.soft,
                    }}
                  >
                    <div className="pillars-scrollpin__icon-wrapper">
                      <Icon size={22} />
                    </div>
                    <span className="pillars-scrollpin__tag">{it.tag}</span>
                    <h3 className="pillars-scrollpin__title">{it.title}</h3>
                    <p className="pillars-scrollpin__detail">{it.detail}</p>
                    <div className="pillars-scrollpin__outputs">
                      {it.outputs.map((o) => (
                        <span key={o} className="pillars-scrollpin__output">
                          {o}
                        </span>
                      ))}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          <div className="hobbies-circle-container" data-reveal>
            <div className="hobbies-circle">
              <svg className="hobbies-circle__svg" viewBox="-250 -250 500 500">
                {Array.from({ length: hobbies.items.length }).map((_, i) => {
                  const a0 = (i / hobbies.items.length) * Math.PI * 2;
                  const a1 = ((i + 1) / hobbies.items.length) * Math.PI * 2;
                  const r = 180;
                  const x0 = Math.cos(a0) * r;
                  const y0 = Math.sin(a0) * r;
                  const x1 = Math.cos(a1) * r;
                  const y1 = Math.sin(a1) * r;
                  return (
                    <path
                      key={i}
                      d={`M ${x0} ${y0} A ${r} ${r} 0 0 1 ${x1} ${y1}`}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="0.5"
                      opacity="0.2"
                    />
                  );
                })}
              </svg>

              <div className="hobbies-circle__center">
                <span className="hobbies-circle__label">{hobbies.label}</span>
                <h3 className="hobbies-circle__title">
                  {activeHobby === null ? hobbies.title : hobbyFocus.name}
                </h3>
                <div className="hobbies-circle__focus">{hobbyFocus.detail}</div>
              </div>

              {hobbies.items.map((h, i) => {
                const angle = (i / hobbies.items.length) * Math.PI * 2 - Math.PI / 2;
                const radius = 230;
                const x = 250 + Math.cos(angle) * radius;
                const y = 250 + Math.sin(angle) * radius;
                const dotColor =
                  i % 2 === 0
                    ? "rgba(124, 140, 166, 0.34)"
                    : "rgba(233, 238, 246, 0.22)";

                return (
                  <div
                    key={h.name}
                    className="hobbies-circle__dot"
                    style={{
                      left: `calc(${(x / 500) * 100}% )`,
                      top: `calc(${(y / 500) * 100}% )`,
                      "--dot-color": dotColor,
                    }}
                    onMouseEnter={() => setActiveHobby(i)}
                    onMouseLeave={() => setActiveHobby(null)}
                    onClick={() => setActiveHobby((v) => (v === i ? null : i))}
                    role="button"
                    tabIndex={0}
                    aria-label={h.name}
                  >
                    <div className="hobbies-circle__dot-inner" />
                  </div>
                );
              })}
            </div>
          </div>

          {about.quote ? (
            <div className="about-immersive__quote" data-reveal>
              <p style={{ margin: 0 }}>{about.quote}</p>
            </div>
          ) : null}
        </div>
      </section>

      <section id="contact" className="contact-section-luxe" ref={contactRef}>
        <div className="contact-interactive-bg" aria-hidden="true">
          <svg className="contact-grid-svg" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%" }}>
            <defs>
              <pattern
                id="contact-grid-pattern"
                x="0"
                y="0"
                width="60"
                height="60"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 60 0 L 0 0 0 60"
                  fill="none"
                  stroke="rgba(124, 140, 166, 0.12)"
                  strokeWidth="1"
                />
              </pattern>
            </defs>
            <rect
              width="100%"
              height="100%"
              fill="url(#contact-grid-pattern)"
            />
          </svg>
          {/* reduced decorative effects for finance-first feel */}
        </div>

        <div className="contact-container-luxe">
          <div className="contact-content-luxe">
            <div className="contact-header-luxe" data-reveal>
              <div className="contact-label-luxe">
                <Sparkles size={14} />
                <span>GET IN TOUCH</span>
              </div>
              <h2 className="contact-title-luxe">
                <span>Let&apos;s create</span>
                <br />
                <span className="contact-title-gradient">something amazing</span>
              </h2>
              <p className="contact-description-luxe">
                I&apos;m always interested in finance internships, valuation projects, and analytical work
                that ships decisions, not just spreadsheets. If you want to collaborate, reach out.
              </p>
            </div>

            <LuxeContactForm />

            <div style={{ marginTop: 30, display: "flex", gap: 12, flexWrap: "wrap" }}>
              <a
                className="luxe-nav__link"
                href={person.linkedinUrl}
                target="_blank"
                rel="noreferrer"
              >
                LinkedIn
              </a>
              <a className="luxe-nav__link" href={resumeUrl} target="_blank" rel="noreferrer">
                Download CV
              </a>
            </div>

            <div
              style={{
                marginTop: 36,
                paddingTop: 18,
                borderTop: "1px solid rgba(255,182,217,0.14)",
                color: "rgba(255,255,255,0.55)",
                fontSize: 12,
                display: "flex",
                justifyContent: "space-between",
                gap: 14,
                flexWrap: "wrap",
              }}
            >
              <span>
                {person.name} • {new Date().getFullYear()}
              </span>
              <span>{person.location}</span>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        /* reveal micro-animations */
        [data-reveal]{
          opacity: 0;
          transform: translateY(24px);
          transition: opacity .8s cubic-bezier(.22,1,.36,1), transform .8s cubic-bezier(.22,1,.36,1);
          will-change: transform;
        }
        .is-revealed{ opacity: 1; transform: translateY(0); }
      `}</style>
    </div>
  );
}
